import { ReactNode, createContext, useContext, useState } from "react";
import {
  useUploadBuildingsList,
  useUploadBuildingsWiaGoogle,
  useUploadPriceList,
} from "../api/mutation";
import { Alert, Spin } from "antd";
import { toast } from "react-toastify";
import { queryClient } from "../utils";
import { activityLogger } from "../api/axios";
import { usePermissions } from "./Permissions.context";

interface UploadContextType {
  uploadBuildingsWiaDatabase: (data: FormData) => void;
  uploadPriceList: (data: FormData) => void;
  uploadBuildingsWiaGoogle: (data: FormData) => void;
  isBuildingsUploading: boolean;
  isPriceListUploading: boolean;
}

const UploadContext = createContext<UploadContextType | null>(null);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isBuildingsUploading, setIsBuildingsUploading] = useState(false);
  const [isPriceListUploading, setIsPriceListUploading] = useState(false);
  const { user } = usePermissions();
  const [isBuildingsAlertOpen, setIsBuildingsAlertOpen] = useState(false);
  const [isPriceListAlertOpen, setIsPriceListAlertOpen] = useState(false);

  const { mutate: uploadBuildingsMutate } = useUploadBuildingsList();
  const { mutate: uploadBuildingsWiGoogleMutate } =
    useUploadBuildingsWiaGoogle();
  const { mutate: uploadPriceListMutate } = useUploadPriceList();
  const uploadBuildingsWiaGoogle = (data: FormData) => {
    setIsBuildingsUploading(true);
    uploadBuildingsWiGoogleMutate(data, {
      onSuccess: (res: any) => {
        setIsBuildingsUploading(false);
        setIsBuildingsAlertOpen(true);
        toast.success(
          `File validated successfully. ${res?.result?.count || 0} buildings uploaded. ${res?.errors?.length || 0} errors during validation`
        );
        queryClient.invalidateQueries({ queryKey: ["buildings"] });
        queryClient.refetchQueries({ queryKey: ["buildings"] });
        try {
          return activityLogger.post("activityLogs", {
            userId: user?.id || 0,
            text: "Uploaded buildings",
          });
        } catch (error) {
          console.log(error);
        }
      },
      onError: () => {
        setIsBuildingsUploading(false);
      },
    });
  };
  const uploadBuildingsWiaDatabase = (data: FormData) => {
    setIsBuildingsUploading(true);
    uploadBuildingsMutate(data, {
      onSuccess: (res: any) => {
        setIsBuildingsUploading(false);
        setIsBuildingsAlertOpen(true);
        toast.success(
          `File validated successfully. ${res?.result?.count || 0} buildings uploaded. ${res?.errors?.length || 0} errors during validation`
        );
        queryClient.invalidateQueries({ queryKey: ["buildings"] });
        queryClient.refetchQueries({ queryKey: ["buildings"] });
        try {
          return activityLogger.post("activityLogs", {
            userId: user?.id || 0,
            text: "Uploaded buildings",
          });
        } catch (error) {
          console.log(error);
        }
      },
      onError: () => {
        setIsBuildingsUploading(false);
      },
    });
  };

  const uploadPriceList = (data: FormData) => {
    setIsPriceListUploading(true);
    uploadPriceListMutate(data, {
      onSuccess: (res: any) => {
        setIsPriceListUploading(false);
        setIsPriceListAlertOpen(true);
        toast.success(
          `File validated successfully. ${res?.result?.count || 0} items uploaded. ${res?.errors?.length || 0} errors during validation`
        );
        queryClient.refetchQueries();
        queryClient.invalidateQueries();
        try {
          return activityLogger.post("activityLogs", {
            userId: user?.id || 0,
            text: "Uploaded Price List",
          });
        } catch (error) {
          console.log(error);
        }
      },
      onError: () => {
        setIsPriceListUploading(false);
      },
    });
  };

  return (
    <UploadContext.Provider
      value={{
        uploadBuildingsWiaDatabase,
        uploadPriceList,
        isBuildingsUploading,
        isPriceListUploading,
        uploadBuildingsWiaGoogle,
      }}
    >
      {children}
      <div className="fixed left-1 bottom-3 flex flex-col gap-2">
        {isBuildingsUploading && (
          <div
            className="rounded-lg bg-white max-w-fit h-fit flex z-50 items-center shadow-sm cursor-pointer"
            onClick={() => setIsBuildingsAlertOpen(!isBuildingsAlertOpen)}
          >
            <Spin
              className="p-3 px-4 flex text-white"
              tip="Uploading buildings list..."
            />
            {isBuildingsAlertOpen && (
              <Alert
                className="bg-transparent border-none"
                message="Processing upload into database"
                description="The Buildings list is uploading in the background. You can continue using the application while the process completes."
                type="info"
              />
            )}
          </div>
        )}
        {isPriceListUploading && (
          <div
            className="rounded-lg bg-white max-w-fit h-fit flex z-50 items-center shadow-sm cursor-pointer"
            onClick={() => setIsPriceListAlertOpen(!isPriceListAlertOpen)}
          >
            <Spin className="p-3 px-4" tip="Uploading price list..." />
            {isPriceListAlertOpen && (
              <Alert
                className="bg-transparent border-none"
                message="Processing upload into database"
                description="The Price list is uploading in the background. You can continue using the application while the process completes."
                type="info"
              />
            )}
          </div>
        )}
      </div>
    </UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextType => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within a UploadProvider");
  }
  return context;
};
